import React, { useEffect, useMemo, useRef, useState } from "react";
import { createClient, Session } from "@supabase/supabase-js";
import ProfilePage from "./ProfilePage";
import LeaderboardPage from "./LeaderboardPage";
import ChecklistPage from "./ChecklistPage";
import ToolsPage from "./ToolsPage";
import WeaponRandomizerPage from "./WeaponRandomizerPage";
import LegendRandomizerPage from "./LegendRandomizerPage";

// ─── Assets ───────────────────────────────────────────────────────────────────
const recruitIcon = "/ranks/recruit.png";
const memberIcon  = "/ranks/member.png";
const officerIcon = "/ranks/officer.png";
const founderIcon = "/ranks/founder.png";

// ─── Supabase ─────────────────────────────────────────────────────────────────
const SUPABASE_URL      = process.env.REACT_APP_SUPABASE_URL || "";
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY || "";
const USE_SUPABASE      = !!SUPABASE_URL && !!SUPABASE_ANON_KEY;

const supabase = USE_SUPABASE
  ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: {
        persistSession:    true,
        autoRefreshToken:  true,
        detectSessionInUrl: false,
        storageKey:        "quit-auth",
      },
    })
  : null;

// ─── Constants ────────────────────────────────────────────────────────────────
const LOCAL_STORAGE_KEY = "quit-roster-members";

const SOCIAL_LINKS = {
  x:       "https://x.com/quit_bh",
  tiktok:  "https://www.tiktok.com/@quit_bh",
  youtube: "https://www.youtube.com/@quit_bh",
  twitch:  "https://www.twitch.tv/quitbh",
  discord: "https://discord.com/invite/quitbh",
};

const MOD_NAMES: Record<string, string> = {
  "danteak404@gmail.com":       "AK",
  "croxo05@gmail.com":           "Croxo",
  "nerdygamer1979@gmail.com":    "Nerdy",
  "clashcollin04@gmail.com":     "Mafurik",
  "rotmghikenviken@gmail.com":   "Eddy",
  "stellaisaduck@gmail.com":     "Stellar",
  "jayalan379@gmail.com":        "Sora",
  "davidwmcgamer@gmail.com":     "Sloth",
};

function modName(email?: string | null): string {
  if (!email) return "Unknown";
  return MOD_NAMES[email.toLowerCase()] || email;
}

function colorGuildText(text: string): React.ReactNode {
  if (!text) return text;
  const parts = String(text).split(/(\b(?:quit|join)\b)/gi);
  return parts.map((part, i) => {
    const lower = part.toLowerCase();
    if (lower === "quit") return <span key={i} className="guild-word-quit">{part}</span>;
    if (lower === "join") return <span key={i} className="guild-word-join">{part}</span>;
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

// ─── Types ────────────────────────────────────────────────────────────────────
type Role          = "Founder" | "Officer" | "Member" | "Recruit";
type DiscordFilter = "All" | "Missing Discord" | "Has Discord";
type RoleFilter    = "All" | Role;
type SortField     = "tag" | "brawlhallaId" | "joinDate" | null;
type SortDirection = "asc" | "desc";
type GuildFilter   = "quit" | "join";

type Member = {
  id:           number;
  tag:          string;
  discordId:    string;
  brawlhallaId: string;
  role:         Role;
  joinDate:     string;
  notes:        string;
  nameHistory?: string[];
  guild?:       string;
};

type FormState = Omit<Member, "id">;

// ─── Data ─────────────────────────────────────────────────────────────────────
const roleIcons: Record<Role, string> = {
  Recruit: recruitIcon,
  Member:  memberIcon,
  Officer: officerIcon,
  Founder: founderIcon,
};

const roleOrder: Record<Role, number> = {
  Founder: 0,
  Officer: 1,
  Member:  2,
  Recruit: 3,
};

const initialMembers: Member[] = [
  {
    id:           1,
    tag:          "Croxo",
    discordId:    "",
    brawlhallaId: "",
    role:         "Founder",
    joinDate:     "2026-04-08",
    notes:        "Base starter entry for the Quit roster.",
  },
];

const emptyForm: FormState = {
  tag:          "",
  discordId:    "",
  brawlhallaId: "",
  role:         "Recruit",
  joinDate:     "",
  notes:        "",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function normalizeRow(row: any): Member {
  return {
    id:           Number(row.id),
    tag:          row.tag || "",
    discordId:    row.discord_id || "",
    brawlhallaId: row.brawlhalla_id || "",
    role:         (row.role || "Recruit") as Role,
    joinDate:     row.join_date || "",
    notes:        row.notes || "",
    nameHistory:  Array.isArray(row.name_history) ? row.name_history : [],
    guild:        row.guild || "quit",
  };
}

function toDbRow(form: FormState | Member) {
  return {
    tag:          form.tag,
    discord_id:   form.discordId,
    brawlhalla_id: form.brawlhallaId,
    role:         form.role,
    join_date:    form.joinDate || null,
    notes:        form.notes,
  };
}

function formatDate(dateString: string): string {
  if (!dateString) return "—";
  const parts = dateString.split("-");
  if (parts.length !== 3) return dateString;
  const [year, month, day] = parts;
  return `${month}/${day}/${year}`;
}

function getSortArrow(
  activeField: SortField,
  field: Exclude<SortField, null>,
  direction: SortDirection
): string {
  if (activeField !== field) return "";
  return direction === "asc" ? " ↑" : " ↓";
}

type ProfileTab = "overview" | "2v2" | "legends" | "weapons";

function getPlayerRoute(pathname: string): { bhId: string; tab: ProfileTab } | null {
  const match = pathname.match(/^\/player\/([^/]+)(?:\/([^/]+))?$/);
  if (!match) return null;
  const bhId = decodeURIComponent(match[1]);
  const raw  = match[2];
  if (!raw || raw === "overview") return { bhId, tab: "overview" };
  if (raw === "2v2" || raw === "legends" || raw === "weapons") return { bhId, tab: raw };
  return { bhId, tab: "overview" };
}

const isManagerPath          = (p: string) => p === "/manager";
const isLogPath              = (p: string) => p === "/log";
const isLoginPath            = (p: string) => p === "/login";
const isHomePath             = (p: string) => p === "/";
const isRosterPath = (p: string) => p === "/roster" || p === "/roster/quit" || p === "/roster/join";

function rosterGuildFromPath(p: string): GuildFilter {
  return p === "/roster/join" ? "join" : "quit";
}
const isTournamentsPath      = (p: string) => p === "/tournaments";
const isLeaderboardPath      = (p: string) => p === "/leaderboard";
const isApplyPath            = (p: string) => p === "/apply";
const isToolsPath            = (p: string) => p === "/tools";
const isChecklistPath        = (p: string) => p === "/checklist";
const isWeaponRandomizerPath = (p: string) => p === "/weapon-randomizer";
const isLegendRandomizerPath = (p: string) => p === "/legend-randomizer";

// ─── Global Styles ────────────────────────────────────────────────────────────
function GlobalStyles() {
  return (
    <style>{`
      @font-face {
        font-family: "Fireside";
        src: url("/fonts/Fireside.otf") format("opentype");
        font-weight: normal;
        font-style: normal;
        font-display: swap;
      }

      *, *::before, *::after { box-sizing: border-box; }

      :root {
        --accent:       #39d98a;
        --accent-glow:  rgba(57, 217, 138, 0.22);
        --bg:           #050505;
        --bg-card:      rgba(255, 255, 255, 0.05);
        --bg-card-hover:rgba(255, 255, 255, 0.04);
        --bg-input:     #111;
        --bg-modal:     #111111;
        --bg-menu:      #111111;
        --bg-tag:       rgba(255, 255, 255, 0.04);
        --border:       rgba(255, 255, 255, 0.08);
        --border-input: rgba(255, 255, 255, 0.12);
        --text:         #ffffff;
        --text-muted:   #a1a1aa;
        --text-dim:     rgba(255, 255, 255, 0.76);
        --text-link:    rgba(255, 255, 255, 0.45);
        --success:      #86efac;
        --warning:      #fbbf24;
        --danger:       #f87171;
        --danger-dark:  #b91c1c;
        --nav-bg:       rgba(5, 5, 5, 0.72);
        --radius-sm:    10px;
        --radius-md:    16px;
        --radius-lg:    18px;
        --radius-full:  999px;
        --font-brand:   "Fireside", Georgia, serif;
        --font-body:    Arial, sans-serif;
      }

      body {
        margin: 0;
        font-family: var(--font-body);
        background: var(--bg);
        color: var(--text);
      }

      .app {
        min-height: 100vh;
        background: linear-gradient(135deg, #050505 0%, #0b0b0b 40%, #0b2a1d 100%);
        color: var(--text);
        padding: 0 24px 24px;
        font-family: var(--font-body);
      }

      .app--home {
        display: flex;
        flex-direction: column;
        background-image:
          linear-gradient(rgba(5, 5, 5, 0.40), rgba(5, 5, 5, 0.58)),
          url("/qbg.jpeg");
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
        background-attachment: fixed;
      }

      .page-inner--home {
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: center;
        width: 100%;
      }

      .page-inner         { max-width: 1100px; margin: 0 auto; }
      .page-inner--wide   { max-width: 1480px; margin: 0 auto; }
      .page-inner--narrow { max-width: 900px;  margin: 0 auto; }
      .page-inner--login  { max-width: 480px;  margin: 52px auto; }

      .card { background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-md); padding: 16px; margin-bottom: 16px; }
      .card--padded { padding: 28px; }
      .card--profile-header { padding: 24px; }

      .nav-wrap {
        position: sticky; top: 0; z-index: 500;
        margin-left: -24px; margin-right: -24px; margin-bottom: 28px;
        background: var(--nav-bg); backdrop-filter: blur(18px);
        border-bottom: 1px solid var(--border);
      }

      .nav-inner {
        max-width: 1200px; margin: 0 auto; padding: 24px 24px 18px;
        display: flex; align-items: center; justify-content: space-between;
        gap: 18px; flex-wrap: wrap;
      }

      .brand-btn {
        background: transparent; border: none; padding: 0; cursor: pointer;
        color: var(--text); font-family: var(--font-brand); font-size: 42px;
        line-height: 1; letter-spacing: 0.02em;
        text-shadow: 0 0 22px var(--accent-glow);
        display: flex; align-items: center; transform: translateY(3px);
        transition: filter 0.15s;
      }
      .brand-btn:hover { filter: brightness(1.1); }

      .nav-wrap--home { margin-bottom: 0; }
      .nav-wrap--home .nav-inner { justify-content: center; padding: 16px 24px 14px; }

      .home-logo {
        text-align: center;
        margin: 0 0 32px;
      }
      .home-logo__mark {
        font-family: var(--font-brand);
        font-size: 120px;
        line-height: 0.9;
        letter-spacing: 0.02em;
        color: var(--text);
        text-shadow: 0 0 28px var(--accent-glow);
        margin: 0;
        font-weight: 400;
      }

      .nav-right { display: flex; align-items: center; gap: 22px; flex-wrap: wrap; }
      .nav-links { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; }

      .nav-link-btn {
        background: transparent; border: none; color: var(--text-dim); cursor: pointer;
        font-size: 14px; font-weight: 800; letter-spacing: 0.05em; text-transform: uppercase;
        padding: 0; transition: color 0.15s, text-shadow 0.15s;
      }
      .nav-link-btn:hover, .nav-link-btn.active {
        color: var(--text); text-shadow: 0 0 14px rgba(57, 217, 138, 0.38);
      }

      .socials { display: flex; align-items: center; gap: 10px; }

      .social-btn {
        width: 36px; height: 36px; border-radius: var(--radius-full);
        border: 1px solid var(--border); background: rgba(255, 255, 255, 0.05);
        color: var(--text); display: inline-flex; align-items: center; justify-content: center;
        text-decoration: none; transition: transform 0.15s, background 0.15s, border-color 0.15s;
      }
      .social-btn:hover { transform: translateY(-1px); background: rgba(255, 255, 255, 0.1); border-color: rgba(255, 255, 255, 0.24); }

      .input { width: 100%; padding: 10px 12px; border-radius: var(--radius-sm); border: 1px solid var(--border-input); background: var(--bg-input); color: var(--text); font-family: var(--font-body); font-size: 14px; }
      .input:focus { outline: none; border-color: var(--accent); }
      .input--search { padding: 10px 12px 10px 36px; font-size: 15px; }
      .input-wrap { position: relative; width: 320px; max-width: 100%; }
      .input-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--text-link); pointer-events: none; display: flex; align-items: center; }

      .log-toolbar { display: flex; align-items: center; justify-content: center; gap: 10px; margin-bottom: 16px; }
      .log-cal-btn {
        width: 42px; height: 42px; border-radius: var(--radius-sm);
        border: 1px solid var(--border); background: var(--bg-input); color: var(--text);
        cursor: pointer; display: inline-flex; align-items: center; justify-content: center;
      }
      .log-cal-btn.active { border-color: var(--accent); color: var(--accent); }
      .log-cal {
        position: absolute; z-index: 30; margin-top: 8px; right: 0;
        background: var(--bg-modal); border: 1px solid var(--border);
        border-radius: var(--radius-md); padding: 12px; min-width: 260px;
      }
      .log-cal__day {
        width: 34px; height: 34px; border: none; border-radius: 8px;
        background: transparent; color: var(--text); cursor: pointer; font-size: 13px;
      }
      .log-cal__day:hover { background: rgba(255,255,255,0.06); }
      .log-cal__day.today { border: 1px solid var(--accent); color: var(--accent); font-weight: 800; }
      .log-cal__day.selected { background: var(--accent); color: #000; font-weight: 800; }
      .log-cal__day.muted { color: var(--text-muted); opacity: 0.45; }
      textarea.input { resize: vertical; }

      .btn { padding: 10px 14px; border-radius: var(--radius-sm); border: none; background: var(--accent); color: #000; font-weight: 700; cursor: pointer; font-family: var(--font-body); font-size: 14px; transition: filter 0.15s; }
      .btn:hover:not(:disabled) { filter: brightness(1.05); }
      .btn:disabled { opacity: 0.6; cursor: not-allowed; }
      .btn--secondary { background: #2a2a2a; color: var(--text); }
      .btn--danger { background: var(--danger-dark); color: var(--text); }
      .btn--link { background: transparent; border: none; color: var(--text); font-weight: 700; font-size: 15px; padding: 0; cursor: pointer; text-align: left; font-family: var(--font-body); }

      /* Guild toggle */
      .guild-toggle-wrap {
        display: flex;
        justify-content: center;
        margin-bottom: 20px;
      }
      .guild-toggle {
        display: flex;
        gap: 0;
        border-radius: var(--radius-md);
        overflow: hidden;
        border: 1px solid var(--border);
      }
      .guild-toggle__btn {
        padding: 14px 60px;
        border: none;
        background: transparent;
        color: var(--text-muted);
        font-size: 18px;
        font-weight: 700;
        cursor: pointer;
        font-family: var(--font-body);
        transition: all 0.15s;
        letter-spacing: 0.05em;
      }
      .guild-toggle__btn:first-child { border-right: 1px solid var(--border); }
      .guild-toggle__btn:hover { color: var(--text); background: rgba(255,255,255,0.05); }
      .guild-toggle__btn.active { background: var(--accent); color: #000; }
      .guild-toggle__btn.active-quit { background: #aa3b3b; color: #fff; }
      .guild-toggle__btn.active-join { background: #00fdfd; color: #111; }
      .guild-word-quit { color: #aa3b3b; font-weight: 700; }
      .guild-word-join { color: #00fdfd; font-weight: 700; }

      .chip { display: inline-block; padding: 4px 10px; border-radius: var(--radius-full); border: 1px solid var(--border); background: var(--bg-tag); color: #d4d4d8; font-size: 13px; }
      .chip--lg { padding: 6px 12px; font-size: 13px; }

      .label-accent { color: var(--accent); font-size: 12px; letter-spacing: 3px; text-transform: uppercase; }

      .stat-box { background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-md); padding: 16px; flex: 1; min-width: 180px; }
      .stat-box__title { color: var(--text-muted); font-size: 14px; }
      .stat-box__value { font-size: 28px; font-weight: 800; margin-top: 6px; }

      .table-wrap { overflow: visible; }

      table.roster { width: 100%; border-collapse: collapse; }
      table.roster.roster--manager { min-width: 1400px; }
      table.roster th { padding: 12px 8px; color: var(--text-muted); text-align: left; border-bottom: 1px solid var(--border); }
      th.sortable { cursor: pointer; user-select: none; }
      th.sortable span { border-bottom: 1px dotted rgba(255,255,255,0.45); }
      table.roster td { padding: 18px 8px; font-size: 15px; vertical-align: middle; }

      tr.roster-row { border-bottom: 1px solid rgba(255,255,255,0.05); height: 64px; transition: background 0.1s; }
      tr.roster-row.hovered { background: var(--bg-card-hover); }
      tr.roster-row--editing { border-bottom: 1px solid rgba(255,255,255,0.05); background: rgba(255,255,255,0.06); }
      tr.roster-row--editing td { padding: 14px 8px; }

      .action-menu-wrap { position: relative; width: 70px; }
      .action-menu-btn { background: #2f2f2f; color: #d4d4d8; border: 1px solid var(--border); border-radius: var(--radius-sm); width: 34px; height: 34px; cursor: pointer; font-size: 18px; line-height: 1; display: flex; align-items: center; justify-content: center; }
      .action-menu { position: fixed; min-width: 132px; background: var(--bg-menu); border: 1px solid var(--border); border-radius: 12px; padding: 6px; box-shadow: 0 10px 30px rgba(0,0,0,0.35); z-index: 4000; }
      .action-menu-item { width: 100%; text-align: left; background: transparent; border: none; border-radius: 8px; padding: 10px 12px; cursor: pointer; font-family: var(--font-body); font-size: 14px; color: var(--text); }
      .action-menu-item:hover { background: rgba(255,255,255,0.06); }
      .action-menu-item--danger { color: var(--danger); }

      .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); display: flex; align-items: center; justify-content: center; padding: 20px; z-index: 1000; }
      .modal-card { width: 100%; max-width: 420px; background: var(--bg-modal); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 20px; box-shadow: 0 20px 60px rgba(0,0,0,0.45); }

      .text-muted   { color: var(--text-muted); }
      .text-success { color: var(--success); }
      .text-warning { color: var(--warning); }
      .text-danger  { color: var(--danger); }

      .flex { display: flex; }
      .flex-wrap { flex-wrap: wrap; }
      .items-center { align-items: center; }
      .justify-between { justify-content: space-between; }
      .gap-8  { gap: 8px; }  .gap-10 { gap: 10px; } .gap-12 { gap: 12px; }
      .gap-16 { gap: 16px; } .gap-20 { gap: 20px; }
      .grid-auto { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 12px; }
      .grid-profile { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 16px; }

      .mt-6{margin-top:6px} .mt-8{margin-top:8px} .mt-10{margin-top:10px} .mt-12{margin-top:12px}
      .mt-14{margin-top:14px} .mt-16{margin-top:16px}
      .mb-0{margin-bottom:0} .mb-6{margin-bottom:6px} .mb-8{margin-bottom:8px}
      .mb-12{margin-bottom:12px} .mb-16{margin-bottom:16px} .mb-18{margin-bottom:18px}
      .mb-20{margin-bottom:20px} .mb-22{margin-bottom:22px} .mb-24{margin-bottom:24px}

      .w-full { width: 100%; }
      .font-bold { font-weight: 700; }
      .font-black { font-weight: 800; }
      .uppercase { text-transform: uppercase; }
      .tracking-wide { letter-spacing: 0.05em; }

      .roster-page-header { padding: 28px; margin-bottom: 20px; }
      .roster-count { color: var(--text-muted); font-size: 13px; margin-bottom: 14px; padding-left: 4px; }

      .profile-avatar { width: 72px; height: 72px; border-radius: 18px; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.04); border: 1px solid var(--border); flex-shrink: 0; }

      .apply-step { line-height: 1.6; }

      .flag--missing { color: var(--warning); font-weight: 700; }
      .flag--linked  { color: var(--success);  font-weight: 700; }

      .role-icon { width: 28px; height: 28px; object-fit: contain; flex-shrink: 0; }
      .role-icon--lg { width: 46px; height: 46px; }

      .btn--discord {
        background: #5865F2;
        color: #fff;
        display: inline-block;
        text-decoration: none;
        padding: 12px 18px;
      }
      .btn--discord:hover { filter: brightness(1.08); }

      .home-grid {
        display: grid;
        grid-template-columns: repeat(5, 1fr);
        gap: 14px;
      }

      .home-card {
        text-align: left;
        background: rgba(8, 10, 10, 0.84);
        border: 1px solid rgba(255, 255, 255, 0.10);
        border-radius: var(--radius-md);
        padding: 22px;
        min-height: 140px;
        cursor: pointer;
        color: var(--text);
        font-family: var(--font-body);
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        gap: 16px;
        backdrop-filter: blur(10px);
        transition: background 0.15s, border-color 0.15s, transform 0.15s;
      }
      .home-card:hover {
        background: rgba(10, 12, 12, 0.92);
        border-color: rgba(255,255,255,0.18);
        transform: translateY(-1px);
      }

      .home-card__top { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; }
      .home-card__title { font-size: 22px; font-weight: 800; margin: 0; }
      .home-card__body { color: var(--text-muted); font-size: 14px; line-height: 1.5; margin: 0; }

      .home-discord {
        display: flex;
        justify-content: center;
        margin-top: 28px;
      }

      @media (max-width: 980px) {
        .home-grid { grid-template-columns: repeat(2, 1fr); }
      }
      @media (max-width: 560px) {
        .home-grid { grid-template-columns: 1fr; }
      }
    `}</style>
  );
}

// ─── Icons ────────────────────────────────────────────────────────────────────
function XIcon() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18.9 2H22L15.2 9.8L23.2 22H16.9L12 14.6L5.8 22H2.7L10 13.6L2.3 2H8.8L13.2 8.7L18.9 2ZM17.8 19.9H19.5L7.9 4H6.1L17.8 19.9Z" /></svg>;
}
function TikTokIcon() {
  return <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><path d="M16.6 5.8C15.7 4.8 15.2 3.5 15.2 2H11.8V15.3C11.8 17 10.4 18.4 8.7 18.4C7 18.4 5.6 17 5.6 15.3C5.6 13.6 7 12.2 8.7 12.2C9.1 12.2 9.4 12.3 9.8 12.4V8.9C9.4 8.8 9 8.8 8.7 8.8C5.1 8.8 2.2 11.7 2.2 15.3C2.2 18.9 5.1 21.8 8.7 21.8C12.3 21.8 15.2 18.9 15.2 15.3V8.6C16.6 9.6 18.3 10.2 20.1 10.2V6.8C18.7 6.8 17.5 6.4 16.6 5.8Z" /></svg>;
}
function YouTubeIcon() {
  return <svg width="19" height="19" viewBox="0 0 24 24" fill="currentColor"><path d="M23 7.1C22.7 5.9 21.8 5 20.6 4.7C18.5 4.1 12 4.1 12 4.1C12 4.1 5.5 4.1 3.4 4.7C2.2 5 1.3 5.9 1 7.1C0.4 9.2 0.4 12 0.4 12C0.4 12 0.4 14.8 1 16.9C1.3 18.1 2.2 19 3.4 19.3C5.5 19.9 12 19.9 12 19.9C12 19.9 18.5 19.9 20.6 19.3C21.8 19 22.7 18.1 23 16.9C23.6 14.8 23.6 12 23.6 12C23.6 12 23.6 9.2 23 7.1ZM9.7 15.4V8.6L15.7 12L9.7 15.4Z" /></svg>;
}
function TwitchIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M4.3 2L2.8 6V20.5H7.8V23H10.7L13.2 20.5H17.1L22.2 15.4V2H4.3ZM20.1 14.4L17.1 17.4H12.4L9.9 19.9V17.4H5.8V4.1H20.1V14.4ZM17.1 7.8V13.2H15V7.8H17.1ZM11.5 7.8V13.2H9.4V7.8H11.5Z" /></svg>;
}
function DiscordIcon() {
  return <svg width="19" height="19" viewBox="0 0 24 24" fill="currentColor"><path d="M20.3 4.4C18.8 3.7 17.2 3.2 15.5 3C15.3 3.4 15 3.9 14.9 4.3C13 4 11.1 4 9.2 4.3C9 3.9 8.8 3.4 8.5 3C6.8 3.2 5.2 3.7 3.7 4.4C0.7 8.9 -0.1 13.2 0.3 17.5C2.3 19 4.2 19.9 6.1 20.5C6.6 19.9 7 19.2 7.4 18.5C6.7 18.3 6.1 18 5.5 17.6C5.7 17.5 5.8 17.4 6 17.2C9.8 19 14.1 19 18 17.2C18.1 17.4 18.3 17.5 18.5 17.6C17.9 18 17.3 18.3 16.6 18.5C17 19.2 17.4 19.9 17.9 20.5C19.8 19.9 21.7 19 23.7 17.5C24.2 12.5 22.9 8.2 20.3 4.4ZM8.1 14.8C7 14.8 6.1 13.8 6.1 12.6C6.1 11.4 7 10.4 8.1 10.4C9.2 10.4 10.1 11.4 10.1 12.6C10.1 13.8 9.2 14.8 8.1 14.8ZM15.9 14.8C14.8 14.8 13.9 13.8 13.9 12.6C13.9 11.4 14.8 10.4 15.9 10.4C17 10.4 17.9 11.4 17.9 12.6C17.9 13.8 17 14.8 15.9 14.8Z" /></svg>;
}
function SearchIcon() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" /><path d="M20 20L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>;
}
function CalendarIcon() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2" /><path d="M3 10H21" stroke="currentColor" strokeWidth="2" /><path d="M8 3V7M16 3V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>;
}

// ─── Small components ─────────────────────────────────────────────────────────
function SocialIconButton({ href, label, children }: { href: string; label: string; children: React.ReactNode }) {
  return <a href={href} target="_blank" rel="noreferrer" aria-label={label} title={label} className="social-btn">{children}</a>;
}

function StatBox({ title, value }: { title: string; value: number }) {
  return <div className="stat-box"><div className="stat-box__title">{title}</div><div className="stat-box__value">{value}</div></div>;
}

function SearchInput({ value, onChange, placeholder, className }: { value: string; onChange: (v: string) => void; placeholder: string; className?: string; }) {
  return <input className={className ?? "input"} placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)} autoComplete="off" />;
}

// ─── TopNav ───────────────────────────────────────────────────────────────────
function TopNav({
  currentPath,
  navigateTo,
  resetRosterSort,
  homeMode = false,
}: {
  currentPath:     string;
  navigateTo:      (path: string) => void;
  resetRosterSort: () => void;
  homeMode?:       boolean;
}) {
  return (
    <div className={`nav-wrap${homeMode ? " nav-wrap--home" : ""}`}>
      <div className="nav-inner">
        {!homeMode && (
          <button type="button" onClick={() => navigateTo("/")} className="brand-btn" aria-label="Go to home">
            quit
          </button>
        )}

        <div className="nav-right">
          {!homeMode && (
            <div className="nav-links">
              {[
                { label: "Roster",      path: "/roster/quit" },
                { label: "Leaderboard", path: "/leaderboard" },
                { label: "Tournaments", path: "/tournaments" },
                { label: "Apply",       path: "/apply"       },
                { label: "Tools",       path: "/tools"       },
              ].map(({ label, path }) => (
                <button
                  key={path}
                  type="button"
                  onClick={() => {
                    if (path.startsWith("/roster")) resetRosterSort();
                    navigateTo(path);
                  }}
                  className={`nav-link-btn${currentPath === path ? " active" : ""}`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          <div className="socials">
            <SocialIconButton href={SOCIAL_LINKS.x}       label="X">       <XIcon />       </SocialIconButton>
            <SocialIconButton href={SOCIAL_LINKS.tiktok}  label="TikTok">  <TikTokIcon />  </SocialIconButton>
            <SocialIconButton href={SOCIAL_LINKS.youtube} label="YouTube"> <YouTubeIcon /> </SocialIconButton>
            <SocialIconButton href={SOCIAL_LINKS.twitch}  label="Twitch">  <TwitchIcon />  </SocialIconButton>
            <SocialIconButton href={SOCIAL_LINKS.discord} label="Discord"> <DiscordIcon /> </SocialIconButton>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────
export default function App() {
  const [members,             setMembers]             = useState<Member[]>(initialMembers);
  const [rosterLog,           setRosterLog]           = useState<any[]>([]);
  const [logLoading,          setLogLoading]          = useState(false);
  const [logSearch,           setLogSearch]           = useState("");
  const [logDay,              setLogDay]              = useState<string | null>(null);
  const [logCalOpen,          setLogCalOpen]          = useState(false);
  const [search,              setSearch]              = useState("");
  const [roleFilter,          setRoleFilter]          = useState<RoleFilter>("All");
  const [discordFilter,       setDiscordFilter]       = useState<DiscordFilter>("All");
  const [guildFilter,         setGuildFilter]         = useState<GuildFilter>("quit");
  const [sortField,           setSortField]           = useState<SortField>(null);
  const [sortDirection,       setSortDirection]       = useState<SortDirection>("asc");
  const [showForm,            setShowForm]            = useState(false);
  const [editingId,           setEditingId]           = useState<number | null>(null);
  const [form,                setForm]                = useState<FormState>(emptyForm);
  const [editingForm,         setEditingForm]         = useState<FormState>(emptyForm);
  const [loading,             setLoading]             = useState(true);
  const [saveState,           setSaveState]           = useState(USE_SUPABASE ? "Supabase connected" : "Local preview mode");
  const [session,             setSession]             = useState<Session | null>(null);
  const [authEmail,           setAuthEmail]           = useState("");
  const [authPassword,        setAuthPassword]        = useState("");
  const [authMessage,         setAuthMessage]         = useState("");
  const [authLoading,         setAuthLoading]         = useState(false);
  const [hoveredRowId,        setHoveredRowId]        = useState<number | null>(null);
  const [openMenuId,          setOpenMenuId]          = useState<number | null>(null);
  const [menuPos,             setMenuPos]             = useState<{ top: number; left: number } | null>(null);
  const [pendingDeleteMember, setPendingDeleteMember] = useState<Member | null>(null);
  const [deleteLoading,       setDeleteLoading]       = useState(false);
  const [currentPath,         setCurrentPath]         = useState(window.location.pathname);

  const addGamertagRef = useRef<HTMLInputElement | null>(null);
  const loginEmailRef  = useRef<HTMLInputElement | null>(null);
  const logCalRef      = useRef<HTMLDivElement | null>(null);

  const resetRosterSort = () => {
    setSortField(null);
    setSortDirection("asc");
    setSearch("");
    setRoleFilter("All");
    setDiscordFilter("All");
  };

  const navigateTo = (path: string) => {
    if (window.location.pathname !== path) {
      window.history.pushState({}, "", path);
    }
    setCurrentPath(path);
  };

  useEffect(() => {
    const handlePopState = () => setCurrentPath(window.location.pathname);
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    if (isRosterPath(currentPath)) setGuildFilter(rosterGuildFromPath(currentPath));
  }, [currentPath]);

  useEffect(() => {
    if (!logCalOpen) return;
    const close = (e: MouseEvent) => {
      if (logCalRef.current && !logCalRef.current.contains(e.target as Node)) {
        setLogCalOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [logCalOpen]);

  useEffect(() => { if (showForm)                setTimeout(() => addGamertagRef.current?.focus(), 0); }, [showForm]);
  useEffect(() => { if (isLoginPath(currentPath)) setTimeout(() => loginEmailRef.current?.focus(), 0); }, [currentPath]);

  useEffect(() => {
    if (!USE_SUPABASE || !supabase) {
      const local = window.localStorage.getItem(LOCAL_STORAGE_KEY);
      setMembers(local ? (JSON.parse(local) as Member[]) : initialMembers);
      setSaveState("Local preview mode");
      setLoading(false);
      return;
    }

    const client = supabase;
    let authSub: { unsubscribe: () => void } | null = null;

    async function boot() {
      const { data: { session: s } } = await client.auth.getSession();
      if (s) setSession(s);
      authSub = client.auth.onAuthStateChange((event, next) => {
        if (event === "SIGNED_OUT") {
          setSession(null);
          return;
        }
        if (next) setSession(next);
      }).data.subscription;
      setLoading(false);
    }

    boot();
    return () => { authSub?.unsubscribe(); };
  }, []);

  useEffect(() => {
    if (!USE_SUPABASE && !loading) {
      window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(members));
    }
  }, [members, loading]);

  useEffect(() => {
    if (!USE_SUPABASE || !supabase) return;
    const client = supabase;

    async function loadMembers() {
      const { data, error } = await client.from("members").select("*").order("id", { ascending: false });
      if (error) { console.error(error); setSaveState("Roster load failed"); }
      else        { setMembers((data || []).map(normalizeRow)); setSaveState("Roster loaded"); }
    }

    loadMembers();

    const onFocus = () => loadMembers();
    const onVis = () => { if (document.visibilityState === "visible") loadMembers(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [session?.user?.id]); // eslint-disable-line

  useEffect(() => {
    if (!USE_SUPABASE || !supabase || !session || !isLogPath(currentPath)) return;
    let cancelled = false;
    setLogLoading(true);
    let query = supabase.from("roster_log").select("*").order("created_at", { ascending: false }).limit(400);
    if (logDay) {
      query = query.gte("created_at", `${logDay}T00:00:00-05:00`).lte("created_at", `${logDay}T23:59:59-05:00`);
    }
    query.then(({ data }) => {
      if (!cancelled) {
        setRosterLog(data || []);
        setLogLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [currentPath, session?.user?.id, logDay]); // eslint-disable-line

  useEffect(() => {
    if (!USE_SUPABASE || !supabase) return;
    const client = supabase;

    const channel = client
      .channel("members-realtime")
      .on("postgres_changes", { event: "*", schema: "public", table: "members" }, async () => {
        const { data, error } = await client.from("members").select("*").order("id", { ascending: false });
        if (!error) setMembers((data || []).map(normalizeRow));
      })
      .subscribe();

    return () => { client.removeChannel(channel); };
  }, []);

  useEffect(() => {
    if (openMenuId === null) return;
    const close = () => { setOpenMenuId(null); setMenuPos(null); }
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [openMenuId]);

  useEffect(() => {
    if (!pendingDeleteMember) return;
    const onEsc = (e: KeyboardEvent) => { if (e.key === "Escape") setPendingDeleteMember(null); };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [pendingDeleteMember]);

  useEffect(() => {
    if (isManagerPath(currentPath) && !loading && USE_SUPABASE && !session) navigateTo("/login");
  }, [currentPath, loading, session]); // eslint-disable-line

  const filteredMembers = useMemo(() => {
    const base = members.filter((m) => {
      const text = `${m.tag} ${m.discordId} ${m.brawlhallaId} ${m.role} ${m.notes}`.toLowerCase();
      const matchesSearch  = text.includes(search.toLowerCase());
      const matchesRole    = roleFilter === "All" || m.role === roleFilter;
      const missing        = !m.discordId?.trim();
      const matchesDiscord = discordFilter === "All" || (discordFilter === "Missing Discord" ? missing : !missing);
      const matchesGuild   = (m.guild || "quit") === guildFilter;
      return matchesSearch && matchesRole && matchesDiscord && matchesGuild;
    });

    return [...base].sort((a, b) => {
      if (sortField === "tag") {
        const [av, bv] = [a.tag.toLowerCase(), b.tag.toLowerCase()];
        return av < bv ? (sortDirection === "asc" ? -1 : 1) : av > bv ? (sortDirection === "asc" ? 1 : -1) : 0;
      }
      if (sortField === "brawlhallaId") {
        return sortDirection === "asc"
          ? (Number(a.brawlhallaId) || 0) - (Number(b.brawlhallaId) || 0)
          : (Number(b.brawlhallaId) || 0) - (Number(a.brawlhallaId) || 0);
      }
      if (sortField === "joinDate") {
        const [at, bt] = [a.joinDate ? new Date(a.joinDate).getTime() : 0, b.joinDate ? new Date(b.joinDate).getTime() : 0];
        return sortDirection === "asc" ? at - bt : bt - at;
      }
      const rc = roleOrder[a.role] - roleOrder[b.role];
      if (rc !== 0) return rc;
      const [at, bt] = [a.joinDate ? new Date(a.joinDate).getTime() : 0, b.joinDate ? new Date(b.joinDate).getTime() : 0];
      if (at !== bt) return at - bt;
      return a.tag.toLowerCase().localeCompare(b.tag.toLowerCase());
    });
  }, [members, search, roleFilter, discordFilter, guildFilter, sortField, sortDirection]);

  const stats = useMemo(() => ({
    total:          members.filter(m => (m.guild || "quit") === guildFilter).length,
    recruits:       members.filter(m => m.role === "Recruit" && (m.guild || "quit") === guildFilter).length,
    memberCount:    members.filter(m => m.role === "Member"  && (m.guild || "quit") === guildFilter).length,
    staff:          members.filter(m => (m.role === "Founder" || m.role === "Officer") && (m.guild || "quit") === guildFilter).length,
    missingDiscord: members.filter(m => !m.discordId?.trim() && (m.guild || "quit") === guildFilter).length,
  }), [members, guildFilter]);

  const playerRoute     = getPlayerRoute(currentPath);
  const currentPlayerId = playerRoute?.bhId ?? null;
  const currentPlayerTab: ProfileTab = playerRoute?.tab ?? "overview";
  const selectedMember  = currentPlayerId ? members.find((m) => m.brawlhallaId === currentPlayerId) : null;

  const setField        = <K extends keyof FormState>(f: K, v: FormState[K]) => setForm(p => ({ ...p, [f]: v }));
  const setEditingField = <K extends keyof FormState>(f: K, v: FormState[K]) => setEditingForm(p => ({ ...p, [f]: v }));

  const toggleSort = (field: Exclude<SortField, null>) => {
    if (sortField !== field) { setSortField(field); setSortDirection("asc"); return; }
    if (sortDirection === "asc") { setSortDirection("desc"); return; }
    setSortField(null); setSortDirection("asc");
  };

  const navigateToProfile = (member: Member) => {
    if (!member.brawlhallaId) return;
    navigateTo(`/player/${encodeURIComponent(member.brawlhallaId)}`);
  };

  const startAdd = () => { setEditingId(null); setEditingForm(emptyForm); setForm(emptyForm); setShowForm(true); };
  const startEdit = (member: Member) => { setEditingId(member.id); setEditingForm({ tag: member.tag, discordId: member.discordId, brawlhallaId: member.brawlhallaId, role: member.role, joinDate: member.joinDate, notes: member.notes }); setOpenMenuId(null); setMenuPos(null); };
  const cancelInlineEdit = () => { setEditingId(null); setEditingForm(emptyForm); };

  const signIn = async () => {
    if (!USE_SUPABASE || !supabase) return;
    setAuthLoading(true);
    setAuthMessage("");
    const { error } = await supabase.auth.signInWithPassword({ email: authEmail, password: authPassword });
    if (error) { setAuthMessage(error.message); }
    else        { setAuthMessage(""); setSaveState("Signed in"); navigateTo("/manager"); }
    setAuthLoading(false);
  };

  const signOut = async () => {
    if (!USE_SUPABASE || !supabase) return;
    await supabase.auth.signOut();
    setMembers([]);
    setSaveState("Signed out");
    navigateTo("/");
  };

  const writeRosterLog = async (action: string, memberId: number | null, tag: string, before: unknown, after: unknown) => {
    if (!USE_SUPABASE || !supabase) return;
    const email = session?.user?.email || "";
    await supabase.from("roster_log").insert([{
      actor_email: email,
      actor_name:  modName(email),
      action,
      member_id:   memberId,
      tag,
      before,
      after,
    }]);
  };

  const saveMember = async () => {
    if (!form.tag.trim()) return;
    const payload = { ...form, joinDate: form.joinDate || new Date().toISOString().slice(0, 10) };

    if (USE_SUPABASE && supabase) {
      const { data, error } = await supabase.from("members").insert([toDbRow(payload)]).select().maybeSingle();
      if (error || !data) {
        console.error(error);
        setSaveState(error?.message || "Save blocked (check Supabase RLS update/insert policy)");
        return;
      }
      setMembers(prev => [normalizeRow(data), ...prev.filter(m => m.id !== Number(data.id))]);
      await writeRosterLog("add", Number(data.id), payload.tag, null, toDbRow(payload));
      setSaveState("Saved to Supabase");
    } else {
      setMembers(prev => [{ id: Date.now(), ...payload }, ...prev]);
      setSaveState("Saved locally in browser");
    }

    setShowForm(false);
    setForm(emptyForm);
  };

  const saveInlineEdit = async (id: number) => {
    if (!editingForm.tag.trim()) return;

    if (USE_SUPABASE && supabase) {
      const { data, error } = await supabase
        .from("members")
        .update(toDbRow(editingForm))
        .eq("id", id)
        .select()
        .maybeSingle();
      if (error || !data) {
        console.error(error);
        setSaveState(error?.message || "Save blocked (check Supabase RLS update policy)");
        return;
      }
      const prev = members.find(m => m.id === id) || null;
      setMembers(prevMembers => prevMembers.map(m => m.id === id ? normalizeRow(data) : m));
      await writeRosterLog("edit", id, editingForm.tag, prev ? toDbRow(prev) : null, toDbRow(editingForm));
      setSaveState("Saved to Supabase");
    } else {
      setMembers(prev => prev.map(m => m.id === id ? { id, ...editingForm } : m));
      setSaveState("Saved locally in browser");
    }

    setEditingId(null);
    setEditingForm(emptyForm);
  };

  const requestDeleteMember = (member: Member) => { setPendingDeleteMember(member); setOpenMenuId(null); };

  const confirmDeleteMember = async () => {
    if (!pendingDeleteMember) return;
    const id = pendingDeleteMember.id;
    setDeleteLoading(true);

    if (USE_SUPABASE && supabase) {
      const { error } = await supabase.from("members").delete().eq("id", id);
      if (error) { console.error(error); setSaveState("Delete failed"); setDeleteLoading(false); return; }
      await writeRosterLog("delete", id, pendingDeleteMember.tag, toDbRow(pendingDeleteMember), null);
      setSaveState("Deleted from Supabase");
    } else {
      setSaveState("Deleted locally in browser");
    }

    setMembers(prev => prev.filter(m => m.id !== id));
    setOpenMenuId(null);
    setPendingDeleteMember(null);
    setDeleteLoading(false);
  };

  const renderShell = (children: React.ReactNode, homeMode = false) => (
    <div className={homeMode ? "app app--home" : "app"}>
      <GlobalStyles />
      <TopNav currentPath={currentPath} navigateTo={navigateTo} resetRosterSort={resetRosterSort} homeMode={homeMode} />
      {children}
    </div>
  );

  // ─── Guild Toggle ─────────────────────────────────────────────────────────
  const GuildToggle = () => (
    <div className="guild-toggle-wrap">
      <div className="guild-toggle">
        <button
          className={`guild-toggle__btn${guildFilter === "quit" ? " active-quit" : ""}`}
          onClick={() => {
            setGuildFilter("quit");
            if (isRosterPath(currentPath)) navigateTo("/roster/quit");
          }}
        >
          Quit
        </button>
        <button
          className={`guild-toggle__btn${guildFilter === "join" ? " active-join" : ""}`}
          onClick={() => {
            setGuildFilter("join");
            if (isRosterPath(currentPath)) navigateTo("/roster/join");
          }}
        >
          Join
        </button>
      </div>
    </div>
  );

  const renderRosterTable = (editable: boolean) => (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>{editable ? "Member Roster" : "Roster"}</h2>
      {!editable && <div className="roster-count">{filteredMembers.length} total members</div>}
      {loading ? (
        <div className="text-muted">Loading roster...</div>
      ) : (
        <div className="table-wrap">
          <table className={`roster${editable ? " roster--manager" : ""}`} style={{ minWidth: editable ? 1400 : 720 }}>
            <thead>
              <tr>
                <th className="sortable" onClick={() => toggleSort("tag")}><span>Gamertag{getSortArrow(sortField, "tag", sortDirection)}</span></th>
                {editable && <th>Discord ID</th>}
                <th className="sortable" onClick={() => toggleSort("brawlhallaId")}><span>Brawlhalla ID{getSortArrow(sortField, "brawlhallaId", sortDirection)}</span></th>
                <th>Position</th>
                <th className="sortable" onClick={() => toggleSort("joinDate")}><span>Join Date{getSortArrow(sortField, "joinDate", sortDirection)}</span></th>
                {editable && <th>Notes</th>}
                {editable && <th>Flag</th>}
                {editable && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filteredMembers.map((member) => {
                const missingDiscord = !member.discordId?.trim();
                const isEditing = editable && editingId === member.id;

                if (isEditing) {
                  return (
                    <tr key={member.id} className="roster-row--editing">
                      <td>
                        <div className="flex items-center gap-10">
                          <img src={roleIcons[editingForm.role]} alt={editingForm.role} className="role-icon" />
                          <input className="input" value={editingForm.tag} onChange={e => setEditingField("tag", e.target.value)} placeholder="Gamertag" />
                        </div>
                      </td>
                      <td><input className="input" value={editingForm.discordId}    onChange={e => setEditingField("discordId",    e.target.value)} placeholder="Discord ID"    /></td>
                      <td><input className="input" value={editingForm.brawlhallaId} onChange={e => setEditingField("brawlhallaId", e.target.value)} placeholder="Brawlhalla ID" /></td>
                      <td>
                        <select className="input" value={editingForm.role} onChange={e => setEditingField("role", e.target.value as Role)}>
                          <option value="Founder">Founder</option>
                          <option value="Officer">Officer</option>
                          <option value="Member">Member</option>
                          <option value="Recruit">Recruit</option>
                        </select>
                      </td>
                      <td><input className="input" type="date" value={editingForm.joinDate} onChange={e => setEditingField("joinDate", e.target.value)} /></td>
                      <td style={{ minWidth: 220 }}><textarea className="input" style={{ minHeight: 70 }} value={editingForm.notes} onChange={e => setEditingField("notes", e.target.value)} placeholder="Notes" /></td>
                      <td className={missingDiscord ? "flag--missing" : "flag--linked"}>{!editingForm.discordId?.trim() ? "Missing Discord" : "Linked"}</td>
                      <td>
                        <div className="flex gap-8" style={{ flexWrap: "nowrap" }}>
                          <button onClick={() => saveInlineEdit(member.id)} className="btn">Save</button>
                          <button onClick={cancelInlineEdit}                className="btn btn--secondary">Cancel</button>
                        </div>
                      </td>
                    </tr>
                  );
                }

                return (
                  <tr
                    key={member.id}
                    className={`roster-row${editable && (hoveredRowId === member.id || openMenuId === member.id) ? " hovered" : ""}`}
                    onMouseEnter={() => editable && setHoveredRowId(member.id)}
                    onMouseLeave={() => editable && setHoveredRowId(cur => openMenuId === member.id ? cur : null)}
                  >
                    <td style={{ fontWeight: 700 }}>
                      <div className="flex items-center gap-10">
                        <img src={roleIcons[member.role]} alt={member.role} className="role-icon" />
                        {member.brawlhallaId
                          ? <button onClick={() => navigateToProfile(member)} className="btn--link">{member.tag}</button>
                          : <span>{member.tag}</span>
                        }
                      </div>
                    </td>
                    {editable && <td>{member.discordId || "—"}</td>}
                    <td>{member.brawlhallaId || "—"}</td>
                    <td><span className="chip">{member.role}</span></td>
                    <td>{formatDate(member.joinDate)}</td>
                    {editable && <td style={{ maxWidth: 260, whiteSpace: "pre-wrap" }}>{member.notes || "—"}</td>}
                    {editable && <td className={missingDiscord ? "flag--missing" : "flag--linked"}>{missingDiscord ? "Missing Discord" : "Linked"}</td>}
                    {editable && (
                      <td className="action-menu-wrap">
                        <button className="action-menu-btn" onClick={e => {
                          e.stopPropagation();
                          const r = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
                          const next = openMenuId === member.id ? null : member.id;
                          setOpenMenuId(next);
                          setMenuPos(next ? { top: r.bottom + 6, left: Math.max(8, r.right - 132) } : null);
                        }}>⋯</button>
                        {openMenuId === member.id && menuPos && (
                          <div className="action-menu" style={{ top: menuPos.top, left: menuPos.left }} onClick={e => e.stopPropagation()}>
                            <button className="action-menu-item" onClick={() => startEdit(member)}>Edit</button>
                            <button className="action-menu-item action-menu-item--danger" onClick={() => requestDeleteMember(member)}>Delete</button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderHomePage = () => renderShell(
    <div className="page-inner page-inner--home">
      <div className="home-logo">
        <h1 className="home-logo__mark">quit</h1>
      </div>
      <div className="home-grid">
        <button type="button" className="home-card" onClick={() => navigateTo("/roster/quit")}>
          <div className="home-card__title">Roster</div>
          <p className="home-card__body">Quit and Join</p>
        </button>

        <button type="button" className="home-card" onClick={() => navigateTo("/leaderboard")}>
          <div className="home-card__top">
            <div className="home-card__title">Leaderboard</div>
            <span className="chip">Soon</span>
          </div>
          <p className="home-card__body">1v1 and 2v2</p>
        </button>

        <button type="button" className="home-card" onClick={() => navigateTo("/tournaments")}>
          <div className="home-card__top">
            <div className="home-card__title">Tournaments</div>
            <span className="chip">Soon</span>
          </div>
          <p className="home-card__body">Brackets</p>
        </button>

        <button type="button" className="home-card" onClick={() => navigateTo("/apply")}>
          <div className="home-card__title">Apply</div>
          <p className="home-card__body">Open a ticket</p>
        </button>

        <button type="button" className="home-card" onClick={() => navigateTo("/tools")}>
          <div className="home-card__title">Tools</div>
          <p className="home-card__body">Checklist and randomizers</p>
        </button>
      </div>

      <div className="home-discord">
        <a
          href={SOCIAL_LINKS.discord}
          target="_blank"
          rel="noreferrer"
          className="btn btn--discord"
        >
          Join Discord
        </a>
      </div>
    </div>
  , true);

  const renderLoginPage = () => renderShell(
    <div className="page-inner--login">
      <div className="card">
        <button onClick={() => navigateTo("/")} className="btn btn--secondary mb-16">← Back to Home</button>
        <div className="label-accent mb-8">Quit</div>
        <h1 style={{ marginTop: 0, fontSize: 36 }}>Mod Login</h1>
        <p className="text-muted">Approved mods can sign in here to access the Roster Manager.</p>
        <form onSubmit={e => { e.preventDefault(); signIn(); }} style={{ display: "grid", gap: 12, marginTop: 16 }}>
          <input ref={loginEmailRef} className="input" placeholder="Email"    value={authEmail}    onChange={e => setAuthEmail(e.target.value)} />
          <input                     className="input" placeholder="Password" value={authPassword} onChange={e => setAuthPassword(e.target.value)} type="password" />
          <button type="submit" className="btn" disabled={authLoading}>{authLoading ? "Signing In..." : "Sign In"}</button>
        </form>
        <div className={`mt-12 ${authMessage ? "text-warning" : "text-muted"}`}>{authMessage || "Use a mod email that exists in Supabase Auth."}</div>
      </div>
    </div>
  );

  const renderRosterPage = () => renderShell(
    <div className="page-inner">
      <div className="card roster-page-header">
        <div className="label-accent mb-12">QUIT</div>
        <div className="flex justify-between items-center gap-20 flex-wrap">
          <h1 style={{ margin: 0, fontSize: 46, lineHeight: 1.05 }}>Roster</h1>
          <button onClick={() => navigateTo("/login")} className="btn" style={{ marginLeft: "auto" }}>Mod Login</button>
        </div>
      </div>
      <GuildToggle />
      <div className="mb-12">
        <div style={{ position: "relative", maxWidth: 500, margin: "0 auto" }}>
          <span className="input-icon"><SearchIcon /></span>
          <SearchInput className="input input--search" placeholder="Search players..." value={search} onChange={setSearch} />
        </div>
      </div>
      {renderRosterTable(false)}
    </div>
  );

  const renderApplyPage = () => renderShell(
    <div className="page-inner--narrow">
      <div className="card card--padded">
        <div className="label-accent mb-12">QUIT</div>
        <h1 style={{ margin: 0, fontSize: 46, lineHeight: 1.05 }}>Apply</h1>
        <p className="text-muted mb-22" style={{ fontSize: 16, lineHeight: 1.6, marginTop: 14 }}>
          Want to join quit? Follow the steps below to apply through our Discord.
        </p>
        <div style={{ display: "grid", gap: 14 }}>
          {[
            { title: "1. Join the Discord", body: "Join the official quit Discord server using the button below.", cta: { href: SOCIAL_LINKS.discord, label: "Join Discord" } },
            { title: "2. Find the #apply channel", body: <>Once you are in the server, look for the <strong style={{ color: "white" }}>#apply</strong> channel.</> },
            { title: "3. Create a ticket", body: "In the apply channel, create an application ticket. This will open a private channel where staff can review your application." },
            { title: "4. Fill out your info", body: "Fill out the requested information in the ticket. Make sure your Brawlhalla name, Discord name, region, and any other required details are accurate." },
          ].map(({ title, body, cta }: any) => (
            <div key={title} className="card apply-step">
              <h2 style={{ marginTop: 0 }}>{title}</h2>
              <p className="text-muted mb-0" style={{ lineHeight: 1.6 }}>{body}</p>
              {cta && <a href={cta.href} target="_blank" rel="noreferrer" className="btn" style={{ display: "inline-block", textDecoration: "none", marginTop: 6 }}>{cta.label}</a>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderComingSoonPage = (title: string, description: string) => renderShell(
    <div className="page-inner--narrow">
      <div className="card card--padded">
        <div className="label-accent mb-12">QUIT</div>
        <h1 style={{ margin: 0, fontSize: 46, lineHeight: 1.05 }}>{title}</h1>
        <p className="text-muted mb-0" style={{ fontSize: 16, lineHeight: 1.6 }}>{description}</p>
      </div>
    </div>
  );

  const chicagoYmd = (d: Date) =>
    new Intl.DateTimeFormat("en-CA", { timeZone: "America/Chicago", year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
  const todayChicago = chicagoYmd(new Date());

  const visibleLog = rosterLog.filter((row) => {
    if (!logSearch.trim()) return true;
    const q = logSearch.toLowerCase();
    return [row.actor_name, row.actor_email, row.action, row.tag, JSON.stringify(row.before), JSON.stringify(row.after)]
      .join(" ")
      .toLowerCase()
      .includes(q);
  });

  const renderLogPage = () => {
    const focus = logDay ? new Date(`${logDay}T12:00:00`) : new Date();
    const year = focus.getFullYear();
    const month = focus.getMonth();
    const first = new Date(year, month, 1);
    const startWeekday = first.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells: { label: string; ymd: string | null; muted: boolean }[] = [];
    for (let i = 0; i < startWeekday; i++) cells.push({ label: "", ymd: null, muted: true });
    for (let d = 1; d <= daysInMonth; d++) {
      const ymd = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      cells.push({ label: String(d), ymd, muted: false });
    }

    return renderShell(
    <div className="page-inner--wide">
      <div className="mb-24">
        <div className="label-accent mb-8">Quit</div>
        <div className="flex justify-between items-center gap-12 flex-wrap">
          <div>
            <h1 style={{ margin: 0, fontSize: 42 }}>Roster Log</h1>
            <p className="text-muted mb-0">Log of all changes made to roster. Snapshot @12:00am CST</p>
          </div>
          <div className="flex gap-10 flex-wrap">
            <button onClick={() => navigateTo("/manager")} className="btn btn--secondary">Back to Manager</button>
            <button onClick={signOut} className="btn btn--secondary">Sign Out</button>
          </div>
        </div>
      </div>

      <div className="log-toolbar">
        <div style={{ position: "relative", maxWidth: 500, width: "100%" }}>
          <span className="input-icon"><SearchIcon /></span>
          <SearchInput className="input input--search" placeholder="Search log..." value={logSearch} onChange={setLogSearch} />
        </div>
        <div style={{ position: "relative" }} ref={logCalRef}>
          <button type="button" className={`log-cal-btn${logDay ? " active" : ""}`} onClick={() => setLogCalOpen(v => !v)} aria-label="Pick a day">
            <CalendarIcon />
          </button>
          {logCalOpen && (
            <div className="log-cal">
              <div className="flex justify-between items-center mb-12">
                <button className="btn btn--secondary" type="button" onClick={() => setLogDay(`${month === 0 ? year - 1 : year}-${String(month === 0 ? 12 : month).padStart(2, "0")}-01`)}>‹</button>
                <div style={{ fontWeight: 700 }}>{focus.toLocaleString("en-US", { month: "long", year: "numeric" })}</div>
                <button className="btn btn--secondary" type="button" onClick={() => setLogDay(`${month === 11 ? year + 1 : year}-${String(month === 11 ? 1 : month + 2).padStart(2, "0")}-01`)}>›</button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
                {["S","M","T","W","T","F","S"].map((d) => <div key={d} className="text-muted" style={{ textAlign: "center", fontSize: 11 }}>{d}</div>)}
                {cells.map((c, i) => (
                  <button
                    key={i}
                    type="button"
                    disabled={!c.ymd}
                    className={`log-cal__day${c.ymd === todayChicago ? " today" : ""}${c.ymd && c.ymd === logDay ? " selected" : ""}${c.muted ? " muted" : ""}`}
                    onClick={() => { if (c.ymd) { setLogDay(c.ymd); setLogCalOpen(false); } }}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              <button type="button" className="btn btn--secondary w-full mt-12" onClick={() => { setLogDay(null); setLogCalOpen(false); }}>All days</button>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        {logLoading ? (
          <div className="text-muted">Loading log...</div>
        ) : visibleLog.length === 0 ? (
          <div className="text-muted">No log entries yet.</div>
        ) : (
          <div className="table-wrap">
            <table className="roster">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>Action</th>
                  <th>Player</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {visibleLog.map((row) => {
                  const when = row.created_at ? new Date(row.created_at).toLocaleString() : "";
                  const before = row.before || {};
                  const after  = row.after || {};
                  const fields = ["tag", "discord_id", "brawlhalla_id", "role", "join_date", "notes", "guild"];
                  const changes = fields
                    .filter((f) => String(before[f] ?? "") !== String(after[f] ?? ""))
                    .map((f) => `${f}: ${before[f] ?? "-"} → ${after[f] ?? "-"}`)
                    .join("; ");
                  const snapshotCount = row.action === "snapshot" && after.members ? `${after.members.length} players saved` : "";
                  const actionLabel =
                    row.action === "join" ? "joined" :
                    row.action === "switch" ? "switched" :
                    row.action;
                  const guildLabel = after.guild || before.guild || "";
                  const detail =
                    row.action === "edit" ? (changes || "No field change") :
                    snapshotCount ||
                    (row.action === "switch" && before.guild && after.guild ? `${before.guild} → ${after.guild}` :
                    row.action === "join" && guildLabel ? `joined ${guildLabel}` :
                    guildLabel || String(row.action || ""));
                  return (
                    <tr key={row.id} className="roster-row">
                      <td>{when}</td>
                      <td style={{ fontWeight: 700 }}>{row.actor_name || modName(row.actor_email)}</td>
                      <td><span className="chip">{actionLabel}</span></td>
                      <td>{row.tag || "-"}</td>
                      <td className="text-muted" style={{ fontSize: 13 }}>
                        {colorGuildText(detail)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
  };

  const renderManagerPage = () => renderShell(
    <div className="page-inner--wide">
      <div className="mb-24">
        <div className="label-accent mb-8">Quit</div>
        <div className="flex justify-between items-center gap-12 flex-wrap">
          <div>
            <h1 style={{ margin: 0, fontSize: 42 }}>Roster Manager</h1>
            <p className="text-muted mb-6">Only officers can edit roster data here.</p>
            <div className="text-success" style={{ fontSize: 14 }}>{saveState}</div>
            {session?.user?.email && <div className="text-muted mt-6" style={{ fontSize: 13 }}>Signed in as {modName(session.user.email)}</div>}
          </div>
          <div className="flex gap-10 flex-wrap">
            <button onClick={() => navigateTo("/log")} className="btn btn--secondary">Roster Log</button>
            <button onClick={signOut}                 className="btn btn--secondary">Sign Out</button>
          </div>
        </div>
      </div>

      <GuildToggle />
      <div className="flex gap-16 flex-wrap mb-0">
        <StatBox title="Total Members"   value={stats.total}          />
        <StatBox title="Recruits"        value={stats.recruits}       />
        <StatBox title="Members"         value={stats.memberCount}    />
        <StatBox title="Staff"           value={stats.staff}          />
        <StatBox title="Missing Discord" value={stats.missingDiscord} />
      </div>

      <div className="card">
        <div className="flex justify-between items-center gap-12 flex-wrap">
          <div className="flex items-center gap-12">
            <h2 style={{ margin: 0 }}>Filters</h2>
          </div>
          <button onClick={startAdd} className="btn">Add Member</button>
        </div>
        <div className="grid-auto mt-16">
          <SearchInput className="input" placeholder="Search gamertag, IDs, role, notes..." value={search} onChange={setSearch} />
          <select className="input" value={roleFilter}    onChange={e => setRoleFilter(e.target.value as RoleFilter)}>
            <option value="All">All Positions</option>
            <option value="Founder">Founder</option>
            <option value="Officer">Officer</option>
            <option value="Member">Member</option>
            <option value="Recruit">Recruit</option>
          </select>
          <select className="input" value={discordFilter} onChange={e => setDiscordFilter(e.target.value as DiscordFilter)}>
            <option value="All">All Discord Status</option>
            <option value="Missing Discord">Missing Discord</option>
            <option value="Has Discord">Has Discord</option>
          </select>
        </div>
      </div>

      {showForm && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Add Member</h2>
          <form onSubmit={e => { e.preventDefault(); saveMember(); }}>
            <div className="grid-auto">
              <input ref={addGamertagRef} className="input" placeholder="Gamertag"      value={form.tag}          onChange={e => setField("tag",          e.target.value)} />
              <input                      className="input" placeholder="Discord ID"    value={form.discordId}    onChange={e => setField("discordId",    e.target.value)} />
              <input                      className="input" placeholder="Brawlhalla ID" value={form.brawlhallaId} onChange={e => setField("brawlhallaId", e.target.value)} />
              <select                     className="input" value={form.role}            onChange={e => setField("role", e.target.value as Role)}>
                <option value="Founder">Founder</option>
                <option value="Officer">Officer</option>
                <option value="Member">Member</option>
                <option value="Recruit">Recruit</option>
              </select>
              <input className="input" type="date" value={form.joinDate} onChange={e => setField("joinDate", e.target.value)} />
            </div>
            <textarea className="input mt-12" style={{ minHeight: 100 }} placeholder="Notes" value={form.notes} onChange={e => setField("notes", e.target.value)} />
            <div className="flex gap-10 mt-12">
              <button type="submit" className="btn">Save Member</button>
              <button type="button" className="btn btn--secondary" onClick={() => { setShowForm(false); setForm(emptyForm); }}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {renderRosterTable(true)}

      {pendingDeleteMember && (
        <div className="modal-overlay" onClick={() => { if (!deleteLoading) setPendingDeleteMember(null); }}>
          <div className="modal-card" onClick={e => e.stopPropagation()}>
            <div className="text-danger uppercase mb-10" style={{ fontSize: 12, letterSpacing: 2 }}>Confirm Delete</div>
            <h3 style={{ margin: "0 0 8px 0", fontSize: 24 }}>Delete member?</h3>
            <p className="text-muted mb-0" style={{ lineHeight: 1.5 }}>
              This will remove <strong style={{ color: "white" }}>{pendingDeleteMember.tag}</strong> from the roster.
            </p>
            <div className="flex gap-10 mt-20" style={{ justifyContent: "flex-end" }}>
              <button onClick={() => setPendingDeleteMember(null)} disabled={deleteLoading} className="btn btn--secondary">Cancel</button>
              <button onClick={confirmDeleteMember}                disabled={deleteLoading} className="btn btn--danger">{deleteLoading ? "Deleting..." : "Delete"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ── Router ──
  if (loading && USE_SUPABASE && (isHomePath(currentPath) || isRosterPath(currentPath) || isManagerPath(currentPath) || !!currentPlayerId)) {
    return (
      <div className="app">
        <GlobalStyles />
        <TopNav currentPath={currentPath} navigateTo={navigateTo} resetRosterSort={resetRosterSort} />
        <div className="page-inner--wide">
          <div className="card"><div className="text-muted">Loading...</div></div>
        </div>
      </div>
    );
  }

  if (currentPlayerId) return renderShell(
    <ProfilePage member={selectedMember ?? null} bhId={currentPlayerId} tab={currentPlayerTab} navigateTo={navigateTo} supabase={supabase} USE_SUPABASE={USE_SUPABASE} />
  );
  if (isLoginPath(currentPath))            return renderLoginPage();
  if (isManagerPath(currentPath))          return USE_SUPABASE && !session ? renderLoginPage() : renderManagerPage();
  if (isLogPath(currentPath))              return USE_SUPABASE && !session ? renderLoginPage() : renderLogPage();
  if (currentPath === "/roster")           { navigateTo("/roster/quit"); return renderRosterPage(); }
  if (isRosterPath(currentPath))           return renderRosterPage();
  if (isLeaderboardPath(currentPath))      return renderShell(<div className="page-inner"><LeaderboardPage navigateTo={navigateTo} /></div>);
  if (isTournamentsPath(currentPath))      return renderComingSoonPage("Tournaments", "Coming soon...");
  if (isToolsPath(currentPath))            return renderShell(<ToolsPage navigateTo={navigateTo} />);
  if (isChecklistPath(currentPath))        return renderShell(<ChecklistPage navigateTo={navigateTo} />);
  if (isWeaponRandomizerPath(currentPath)) return renderShell(<WeaponRandomizerPage navigateTo={navigateTo} />);
  if (isLegendRandomizerPath(currentPath)) return renderShell(<LegendRandomizerPage navigateTo={navigateTo} />);
  if (isApplyPath(currentPath))            return renderApplyPage();
  return renderHomePage();
}
